import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Modal,
    Button,
    Form,
    Row,
    Col,
    ProgressBar,
    Alert
} from 'react-bootstrap';
import { FiX, FiFile } from 'react-icons/fi';
import { BsCloudArrowUp } from "react-icons/bs";
import './FileUploadModal.css';
import instance from '../../services/baseService';
import { validateFileAndHeaders, EXPECTED_TITLE_ROW, EXPECTED_HEADERS } from './excelValidation';
const BRCFileUploadModal = ({ show, onHide, clientId, reFetch }) => {
    // State management
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [file, setFile] = useState(null); // Changed from files array to single file
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState('');
    const fileInputRef = useRef(null);

    const [isValid, setIsValid] = useState(false);



    // Handle file selection - Modified for single file
    const handleFileSelect = useCallback(async (selectedFiles) => {
        if (selectedFiles.length === 0) return;

        const selectedFile = selectedFiles[0]; // Take only the first file
        try {

            // Validate both file format and headers
            const error = await validateFileAndHeaders(selectedFile);


            if (error) {
                setErrors(prev => ({ ...prev, file: error }));
                setFile(null);
                setIsValid(false);
            } else {
                setIsValid(true);
                setFile({
                    id: Date.now(),
                    file: selectedFile,
                    name: selectedFile.name,
                    size: selectedFile.size,
                    progress: 0
                });
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.file;
                    return newErrors;
                });
            }
        } catch (error) {
            console.error('Validation error:', error);
            setErrors(prev => ({ ...prev, file: 'An unexpected error occurred during validation' }));
            setIsValid(false);
        }
    }, [setErrors, setFile, setIsValid]);

    // Drag and drop handlers
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget === e.target) {
            setIsDragOver(false);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            handleFileSelect(droppedFiles);
        }
    }, [handleFileSelect]);

    // Remove file - Modified for single file
    const removeFile = () => {
        setFile(null);
        // Clear file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Clear file-related errors
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.file;
            delete newErrors.files;
            return newErrors;
        });
    };

    // Validate dates
    const validateDates = () => {
        const newErrors = {};

        if (!startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            newErrors.endDate = 'End date must be after start date';
        }

        return newErrors;
    };

    // Handle form submission with API integration
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous errors
        setErrors({});
        setSuccess('');

        // Validation
        const dateErrors = validateDates();
        const newErrors = { ...dateErrors };

        if (!file) {
            newErrors.file = 'Please select a file to upload';
        }

        if (!clientId) {
            newErrors.submit = 'Client ID is required for upload';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Create FormData for API request
            const formData = new FormData();
            formData.append('file', file.file);
            formData.append('start_date', startDate);
            formData.append('end_date', endDate);

            // Start progress simulation
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev < 90) {
                        return prev + 10;
                    }
                    return prev;
                });
            }, 200);

            // Make API call
            const response = await instance.post(
                `/api/v1/reconciliation/import/${clientId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            // Clear progress interval
            clearInterval(progressInterval);
            setUploadProgress(100);

            // Handle response based on status
            if (response.data && response.data.type === 'success') {
                const { data } = response.data;
                // Handle different status codes
                switch (data.status) {
                    case 1: // success
                        setSuccess('File uploaded and reconciled successfully!');
                        break;
                    case 2: // processing
                        setSuccess('File uploaded successfully! Processing reconciliation...');
                        break;
                    case 5: // success_read
                        setSuccess('File uploaded and read successfully!');
                        break;
                    default:
                        setSuccess('File uploaded successfully!');
                }

                // Wait a moment to show success message
                await new Promise(resolve => setTimeout(resolve, 2500));
                await reFetch();
                resetForm();
                onHide(true, data);

            } else {
                throw new Error(response.data?.message || 'Upload failed');
            }

        } catch (error) {
            console.error('Upload error:', error);

            // Handle different error types
            if (error.response) {
                const { status, data } = error.response;

                if (data && data.data && data.data.status) {
                    // Handle specific status codes from API
                    switch (data.data.status) {
                        case 3: // failed
                            setErrors({ submit: 'Upload failed. Please check your file and try again.' });
                            break;
                        case 4: // validation error
                            setErrors({ submit: 'File validation failed. Please check the file format and content.' });
                            break;
                        case 6: // reconciliation api failed
                            setErrors({ submit: 'Reconciliation process failed. Please try again later.' });
                            break;
                        default:
                            setErrors({ submit: data.message || 'Upload failed. Please try again.' });
                    }
                } else if (status === 400) {
                    setErrors({ submit: 'Invalid file or data. Please check your inputs.' });
                } else if (status === 413) {
                    setErrors({ submit: 'File too large. Please upload a file smaller than 10MB.' });
                } else if (status >= 500) {
                    setErrors({ submit: 'Server error. Please try again later.' });
                } else {
                    setErrors({ submit: data?.message || 'Upload failed. Please try again.' });
                }
            } else if (error.request) {
                setErrors({ submit: 'Network error. Please check your connection and try again.' });
            } else {
                setErrors({ submit: 'Upload failed. Please try again.' });
            }
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    // Reset form - Modified for single file
    const resetForm = () => {
        setStartDate('');
        setEndDate('');
        setFile(null);
        setErrors({});
        setSuccess('');
        setUploadProgress(0);
        setIsUploading(false);
        setIsDragOver(false);

        // Clear file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle modal close
    const handleClose = () => {
        if (!isUploading) {
            resetForm();
            onHide(false); // Pass false to indicate no upload
        }
    };

    // Clear states when modal is closed
    useEffect(() => {
        if (!show) {
            // Reset form when modal is hidden
            resetForm();
        }
    }, [show]);

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            backdrop="static"
        >
            <Modal.Header closeButton className="border-0 d-flex justify-content-between align-items-start">
                <div className="flex-grow-1 text-start">
                    <Modal.Title className="fw-bold">Upload for Reconciliation</Modal.Title>
                    <p className="text-muted mb-0">Upload Excel file to reconcile with database records.</p>
                </div>
            </Modal.Header>
            <Modal.Body className="p-3">
                <Form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <h5 className="fw-semibold mb-3">Select shipping bill period</h5>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => {
                                            setStartDate(e.target.value);
                                            // Clear start date error when user types
                                            if (errors.startDate) {
                                                setErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.startDate;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        isInvalid={!!errors.startDate}
                                        disabled={isUploading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.startDate}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => {
                                            setEndDate(e.target.value);
                                            // Clear end date error when user types
                                            if (errors.endDate) {
                                                setErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.endDate;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        isInvalid={!!errors.endDate}
                                        disabled={isUploading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.endDate}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    {/* Upload Section */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-semibold mb-0">Upload Icegate File</h5>
                            <a
                                href="/assets/files/ICEGATE data.xlsx"
                                download="ICEGATE data.xlsx"
                                className="btn btn-link text-decoration-none p-0"
                            >
                                Download Sample
                            </a>
                        </div>
                        <div
                            className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => !isUploading && fileInputRef.current?.click()}
                        >
                            <div className="upload-icon">
                                <BsCloudArrowUp />
                            </div>
                            <h6 className="mb-2">
                                <span className="text-primary text-decoration-none">Upload file</span>
                                <span className="text-muted"> or drag and drop</span>
                            </h6>
                            <small className="text-muted">XLS, XLSX up to 10MB</small>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                accept=".xls,.xlsx"
                                onChange={(e) => {
                                    handleFileSelect(e.target.files);
                                    // reset input so selecting the same file again triggers onChange
                                    e.target.value = "";
                                }}
                                className="d-none"
                                disabled={isUploading}
                            />
                        </div>

                        {errors.file && (
                            <Alert variant="danger" className="mt-2 " dismissible
                                onClose={() => setErrors(prev => ({ ...prev, file: null }))}
                            >
                                {errors.file}
                            </Alert>
                        )}
                    </div>

                    {/* Selected File Display - Modified for single file */}
                    {file && (
                        <div className="mb-4">
                            <h6 className="fw-semibold mb-3">Selected File</h6>
                            <div className="selected-files">
                                <div className="file-item">
                                    <div className="d-flex align-items-center">
                                        <FiFile className="text-primary me-2" />
                                        <div className="flex-grow-1">
                                            <div className="file-name">{file.name}</div>
                                            <small className="text-muted">
                                                {formatFileSize(file.size)}
                                            </small>
                                        </div>
                                        {!isUploading && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-danger p-1"
                                                onClick={removeFile}
                                                title="Remove file"
                                            >
                                                <FiX />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <Alert variant="success" className="py-2 mb-3">
                            {success}
                        </Alert>
                    )}

                    {/* Error Messages */}
                    {errors.submit && (
                        <Alert variant="danger" className="py-2 mb-3">
                            {errors.submit}
                        </Alert>
                    )}

                    {/* Progress Section */}
                    {isUploading && (
                        <div className="progress-section mb-4">
                            <h6 className="fw-semibold mb-3">Upload Progress</h6>
                            <ProgressBar
                                now={uploadProgress}
                                className="custom-progress mb-2"
                            />
                            <small className="text-muted">{uploadProgress}% complete</small>
                        </div>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer className='border-0'>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isUploading}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isUploading || !file || !startDate || !endDate || !isValid}
                    className="d-flex align-items-center"
                >
                    {isUploading ? (
                        <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            Uploading...
                        </>
                    ) : (
                        'Upload and Reconcile'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BRCFileUploadModal;